/**
 * Shared OCR text normalization and upload payload for personal documents.
 */

const str = (value) => (value == null || value === "" ? "" : String(value).trim());

// Retained from legacy PassportRegexParser.js (removed) — still used by
// buildUnifiedExtractedJson for passport_copy profile saves and document intelligence
// bridging. Not part of the Tesseract/regex fallback being removed.
export const buildPassportExtractedJson = (parsed = {}) => ({
  passport_number: parsed.passport_number || "",
  surname: parsed.surname || "",
  given_name: parsed.given_name || "",
  nationality: parsed.nationality || "",
  date_of_birth: parsed.date_of_birth || "",
  sex: parsed.sex || "",
  place_of_birth: parsed.place_of_birth || "",
  citizenship_number: parsed.citizenship_number || "",
  father_name: parsed.father_name || "",
  husband_name: parsed.husband_name || "",
  date_of_issue: parsed.date_of_issue || "",
  date_of_expiry: parsed.date_of_expiry || "",
});

const SKIP_JSON_KEYS = new Set([
  "rawText",
  "raw_text",
  "cleaned_text",
  "extracted_json",
  "error",
  "document_type",
]);

/**
 * Education (and similar): only include keys that have real values — JSON varies by document.
 */
export const buildFlexibleExtractedJson = (parsed = {}) => {
  const out = {};

  for (const [key, value] of Object.entries(parsed)) {
    if (SKIP_JSON_KEYS.has(key)) continue;
    if (value == null || value === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    if (
      typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0
    ) {
      continue;
    }
    out[key] = value;
  }

  return out;
};

/**
 * extracted_json: fixed shape for id_card/passport; flexible for education.
 */
export const buildUnifiedExtractedJson = (documentType, parsed = {}) => {
  if (documentType === "id_card") {
    return {
      name: str(parsed.name),
      gender: str(parsed.gender),
      father_name: str(parsed.father_name),
      id_card_number: str(
        parsed.id_card_number || parsed.cnic_no || parsed.cnicNo,
      ),
      date_of_birth: str(parsed.date_of_birth || parsed.dateOfBirth),
      date_of_issue: str(
        parsed.date_of_issue || parsed.issue_date || parsed.issueDate,
      ),
      date_of_expiry: str(
        parsed.date_of_expiry || parsed.expiry_date || parsed.expiryDate,
      ),
    };
  }

  if (documentType === "education_document") {
    const normalized = { ...parsed };
    delete normalized.document_type;
    if (normalized.groupOfSubjects && !normalized.group_of_subjects) {
      normalized.group_of_subjects = normalized.groupOfSubjects;
      delete normalized.groupOfSubjects;
    }
    if (normalized.courseStartDate && !normalized.course_start_date) {
      normalized.course_start_date = normalized.courseStartDate;
      delete normalized.courseStartDate;
    }
    if (normalized.courseEndDate && !normalized.course_end_date) {
      normalized.course_end_date = normalized.courseEndDate;
      delete normalized.courseEndDate;
    }
    return buildFlexibleExtractedJson(normalized);
  }

  if (documentType === "passport_copy") {
    return buildPassportExtractedJson(parsed);
  }

  if (documentType === "cv_work_experience") {
    const normalized = { ...parsed };
    delete normalized.document_type;
    return buildFlexibleExtractedJson(normalized);
  }

  return {};
};

export const toReadableCleanedText = (rawText) => {
  const lines = (rawText || "")
    .replace(/\u00a0/g, " ")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter((line) => {
      if (!line || line.length < 2) return false;
      const meaningful = (line.match(/[A-Za-z0-9\u0600-\u06FF]/g) || []).length;
      if (meaningful / line.length < 0.2) return false;
      if (/^[^A-Za-z0-9\u0600-\u06FF]+$/.test(line)) return false;
      return true;
    });

  return lines.join("\n").trim();
};

export const appendPersonalDocOcrFields = (formData, ocr = {}) => {
  formData.append("raw_text", ocr.raw_text || "");
  formData.append("cleaned_text", ocr.cleaned_text || "");
  formData.append("extracted_json", ocr.extracted_json || "{}");
};
